// ============================================================================
// SESSION HTML EXPORT
// Full-width, left-nav static HTML page for customer sharing.
// ============================================================================

import { api } from '../services/api';
import { ProductRequirements } from '../services/workshop';
import { SessionSummaryData } from '../components/SessionSummaryPanel';
import { SessionDecision } from '../components/live-session/DecisionsTab';
import { DeviceImageMetadata } from '../services/deviceImages';

interface TranscriptEntry {
  id?: string;
  speaker: string;
  avatarRole?: string;
  text: string;
  timestamp: string;
  sourceName?: string;
}

export interface TaskOutputForHTML {
  taskName: string;
  outputs: Array<{ type: string; content: any; source: string }>;
}

export interface HTMLExportOptions {
  includeTranscript?: boolean;
  images?: DeviceImageMetadata[];
  taskOutputs?: TaskOutputForHTML[];
}

interface ExportData {
  sessionId: string;
  sessionName?: string;
  exportedAt: string;
  transcript: TranscriptEntry[];
  decisions: SessionDecision[];
  requirements: ProductRequirements | null;
  summary: SessionSummaryData | null;
  options: HTMLExportOptions;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatTime(ts: string): string {
  try { return new Date(ts).toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ts; }
}

function formatDate(ts: string): string {
  try { return new Date(ts).toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return ts; }
}

function speakerLabel(entry: TranscriptEntry): string {
  if (entry.speaker === 'whiteboard') return entry.sourceName || 'Whiteboard';
  if (entry.speaker === 'avatar') return entry.avatarRole || 'Avatar';
  return 'Facilitator';
}

function speakerInitial(entry: TranscriptEntry): string {
  return speakerLabel(entry)[0].toUpperCase();
}

function priorityBadge(p: string): string {
  const colours: Record<string, string> = { high: '#dc2626', medium: '#d97706', low: '#6b7280' };
  return `<span class="badge" style="background:${colours[p] || '#6b7280'}">${esc(p)}</span>`;
}

// ── Section content builders ──────────────────────────────────────────────────

function buildSummaryContent(decisions: SessionDecision[], summary: SessionSummaryData | null): string {
  const accepted = decisions.filter(d => d.status === 'accepted');

  let html = '';

  if (accepted.length) {
    html += `
      <div class="card">
        <h2 class="card-title">What We Decided</h2>
        <ol class="decision-list">
          ${accepted.map((d, i) => `
            <li class="decision-item">
              <span class="decision-num">${i + 1}</span>
              <div>
                <p class="decision-text">${esc(d.editedStatement || d.statement)}</p>
                ${d.topic ? `<p class="decision-meta">${esc(d.editedTopic || d.topic)}</p>` : ''}
              </div>
            </li>`).join('')}
        </ol>
      </div>`;
  }

  if (summary?.variant === 'structured') {
    if (summary.personas?.length) {
      html += `<div class="card"><h2 class="card-title">Journeys by Persona</h2><div class="sum-persona-grid">`;
      for (const p of summary.personas) {
        html += `
          <div class="sum-persona">
            <div class="sum-persona-header">
              <div class="persona-avatar-sm">${esc(p.role[0].toUpperCase())}</div>
              <p class="sum-persona-role">${esc(p.role)}</p>
            </div>
            <p class="sum-persona-state">${esc(p.currentState)}</p>
            ${p.painPoints?.length ? '<ul class="sum-pain">' + p.painPoints.map(x => '<li>' + esc(x) + '</li>').join('') + '</ul>' : ''}
            ${p.whereItHelps?.length ? '<ul class="sum-helps">' + p.whereItHelps.map(x => '<li>' + esc(x) + '</li>').join('') + '</ul>' : ''}
          </div>`;
      }
      html += `</div></div>`;
    }

    if (summary.nextSteps?.length) {
      html += `
        <div class="card">
          <h2 class="card-title">Next Steps</h2>
          <ul class="next-steps">
            ${summary.nextSteps.map(s => '<li>' + (s.owner ? '<strong>' + esc(s.owner) + ':</strong> ' : '') + esc(s.text) + '</li>').join('')}
          </ul>
        </div>`;
    }

    if (summary.stats && Object.values(summary.stats).some(v => v !== undefined)) {
      const s = summary.stats;
      html += `
        <div class="card">
          <h2 class="card-title">The Process in Numbers</h2>
          <div class="stats-grid">
            ${s.avgProcessingMins !== undefined ? `<div class="stat-card"><p class="stat-val">${s.avgProcessingMins}<span class="stat-unit"> min</span></p><p class="stat-lbl">avg per case</p></div>` : ''}
            ${s.rejectionRate !== undefined ? `<div class="stat-card"><p class="stat-val">${s.rejectionRate}<span class="stat-unit">%</span></p><p class="stat-lbl">rejection rate</p></div>` : ''}
            ${s.multipleConditionsPct !== undefined ? `<div class="stat-card"><p class="stat-val">${s.multipleConditionsPct}<span class="stat-unit">%</span></p><p class="stat-lbl">multiple conditions</p></div>` : ''}
          </div>
        </div>`;
    }
  }

  if (summary?.variant === 'full' && summary.markdownContent) {
    html += `
      <div class="card">
        <h2 class="card-title">Full Session Recap</h2>
        <div class="prose">${esc(summary.markdownContent)}</div>
      </div>`;
  }

  if (!html) {
    html = `<div class="card empty-state"><p>No summary available for this session.</p></div>`;
  }

  return html;
}

function buildPersonasContent(requirements: ProductRequirements | null): string {
  if (!requirements?.personas?.length && !requirements?.userStories?.length) {
    return `<div class="card empty-state"><p>No personas captured for this session.</p></div>`;
  }
  if (!requirements?.personas?.length) {
    // Stories exist but no personas — show all as unassigned
    const stories = requirements?.userStories || [];
    return `
      <div class="card persona-card">
        <div class="persona-header">
          <div class="persona-avatar" style="background:rgba(100,116,139,0.12);color:#64748b">?</div>
          <div>
            <p class="persona-name">Unassigned Stories</p>
            <p class="persona-role-lbl">Not linked to a persona</p>
          </div>
        </div>
        <div class="stories-section" style="border-top:none">
          <p class="label" style="padding:16px 24px 10px">User Stories</p>
          ${stories.map(s => `
            <div class="story-card">
              <div class="story-header">
                <p class="story-text">${esc(s.story)}</p>
                ${priorityBadge(s.priority)}
              </div>
              ${s.acceptanceCriteria?.length ? `
                <ul class="ac-list">
                  ${s.acceptanceCriteria.map(ac => `<li>${esc(ac)}</li>`).join('')}
                </ul>` : ''}
            </div>`).join('')}
        </div>
      </div>`;
  }

  const storiesByPersona: Record<string, typeof requirements.userStories> = {};
  for (const story of requirements.userStories || []) {
    if (!storiesByPersona[story.persona]) storiesByPersona[story.persona] = [];
    storiesByPersona[story.persona].push(story);
  }

  const assignedPersonaKeys = new Set(
    requirements.personas.flatMap(p => [p.name, p.role].filter(Boolean))
  );
  const unassignedStories = (requirements.userStories || []).filter(
    s => !assignedPersonaKeys.has(s.persona)
  );

  return [
    ...requirements.personas.map(persona => {
    const stories = storiesByPersona[persona.name] || storiesByPersona[persona.role] || [];
    return `
      <div class="card persona-card">
        <div class="persona-header">
          <div class="persona-avatar">${esc((persona.name || persona.role || '?')[0].toUpperCase())}</div>
          <div>
            <p class="persona-name">${esc(persona.name)}</p>
            <p class="persona-role-lbl">${esc(persona.role)}</p>
          </div>
        </div>
        ${persona.description ? `<p class="persona-desc">${esc(persona.description)}</p>` : ''}
        <div class="persona-body">
          ${persona.goals?.length ? `
            <div class="persona-section">
              <p class="label">Goals</p>
              <ul class="dot-list">${persona.goals.map(g => `<li>${esc(g)}</li>`).join('')}</ul>
            </div>` : ''}
          ${persona.painPoints?.length ? `
            <div class="persona-section">
              <p class="label">Pain Points</p>
              <ul class="dot-list pain">${persona.painPoints.map(p => `<li>${esc(p)}</li>`).join('')}</ul>
            </div>` : ''}
        </div>
        ${stories.length ? `
          <div class="stories-section">
            <p class="label" style="padding:0 24px 10px">User Stories</p>
            ${stories.map(s => `
              <div class="story-card">
                <div class="story-header">
                  <p class="story-text">${esc(s.story)}</p>
                  ${priorityBadge(s.priority)}
                </div>
                ${s.acceptanceCriteria?.length ? `
                  <ul class="ac-list">
                    ${s.acceptanceCriteria.map(ac => `<li>${esc(ac)}</li>`).join('')}
                  </ul>` : ''}
              </div>`).join('')}
          </div>` : ''}
      </div>`;
    }),
    unassignedStories.length ? `
      <div class="card persona-card">
        <div class="persona-header">
          <div class="persona-avatar" style="background:rgba(100,116,139,0.12);color:#64748b">?</div>
          <div>
            <p class="persona-name">Unassigned Stories</p>
            <p class="persona-role-lbl">Not linked to a persona</p>
          </div>
        </div>
        <div class="stories-section" style="border-top:none">
          <p class="label" style="padding:16px 24px 10px">User Stories</p>
          ${unassignedStories.map(s => `
            <div class="story-card">
              <div class="story-header">
                <p class="story-text">${esc(s.story)}</p>
                ${priorityBadge(s.priority)}
              </div>
              ${s.acceptanceCriteria?.length ? `
                <ul class="ac-list">
                  ${s.acceptanceCriteria.map(ac => `<li>${esc(ac)}</li>`).join('')}
                </ul>` : ''}
            </div>`).join('')}
        </div>
      </div>` : ''
  ].filter(Boolean).join('');
}

function buildTranscriptContent(transcript: TranscriptEntry[]): string {
  if (transcript.length === 0) {
    return `<div class="card empty-state"><p>No transcript available for this session.</p></div>`;
  }

  const messages = transcript.map(entry => {
    const isHuman = entry.speaker === 'human';
    const isWhiteboard = entry.speaker === 'whiteboard';
    const label = speakerLabel(entry);
    const initial = speakerInitial(entry);
    const time = formatTime(entry.timestamp);

    if (isWhiteboard) {
      return `
        <div class="chat-whiteboard">
          <div class="chat-wb-inner">
            <span class="chat-wb-label">📋 ${esc(label)}</span>
            <p class="chat-wb-text">${esc(entry.text)}</p>
            <span class="chat-time">${time}</span>
          </div>
        </div>`;
    }

    if (isHuman) {
      return `
        <div class="chat-row chat-row-human">
          <div class="chat-bubble chat-bubble-human">
            <div class="chat-speaker">${esc(label)}</div>
            <p>${esc(entry.text)}</p>
            <span class="chat-time chat-time-human">${time}</span>
          </div>
          <div class="chat-avatar chat-avatar-human">${initial}</div>
        </div>`;
    }

    // avatar
    return `
      <div class="chat-row chat-row-avatar">
        <div class="chat-avatar chat-avatar-avatar">${initial}</div>
        <div class="chat-bubble chat-bubble-avatar">
          <div class="chat-speaker">${esc(label)}</div>
          <p>${esc(entry.text)}</p>
          <span class="chat-time">${time}</span>
        </div>
      </div>`;
  }).join('');

  return `<div class="chat-window">${messages}</div>`;
}

// ── Optional section builders ─────────────────────────────────────────────────

function buildImagesContent(images: DeviceImageMetadata[]): string {
  if (!images.length) {
    return `<div class="card empty-state"><p>No images captured for this session.</p></div>`;
  }
  return `
    <div class="card">
      <h2 class="card-title">Session Images <span style="font-weight:400;text-transform:none;letter-spacing:0;font-size:11px;color:#94a3b8">(links valid ~1 hour)</span></h2>
      <div class="img-grid">
        ${images.map(img => `
          <a href="${img.presignedUrl}" target="_blank" class="img-card">
            <img src="${img.presignedUrl}" alt="${esc(img.captureLabel || img.imageId)}" class="img-thumb" />
            <p class="img-label">${esc(img.captureLabel || img.deviceId)}</p>
            ${img.captureSource === 'ai-generated' ? `<span class="img-badge">AI</span>` : ''}
          </a>`).join('')}
      </div>
    </div>`;
}

function buildNFRsContent(requirements: ProductRequirements | null): string {
  const nfrs = requirements?.nonFunctionalRequirements ?? [];
  if (!nfrs.length) {
    return `<div class="card empty-state"><p>No non-functional requirements captured for this session.</p></div>`;
  }
  const NFR_CATEGORIES = ['performance', 'security', 'scalability', 'availability', 'usability', 'maintainability', 'compliance'] as const;
  return NFR_CATEGORIES.flatMap(cat => {
    const catNfrs = nfrs.filter(n => n.category === cat);
    if (!catNfrs.length) return [];
    return [`
    <div class="card">
      <div style="padding:16px 24px 8px;border-bottom:1px solid #e2e8f0">
        <p style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#00c7c4">${esc(cat)}</p>
      </div>
      <div style="padding:8px 24px 16px;display:flex;flex-direction:column;gap:12px">
        ${catNfrs.map(n => `
        <div style="border:1px solid #e2e8f0;border-radius:12px;padding:14px 16px">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
            ${priorityBadge(n.priority)}
            <span style="font-size:14px;font-weight:600;color:#052831">${esc(n.title)}</span>
          </div>
          ${n.description ? `<p style="font-size:12px;color:#64748b;margin:0 0 8px">${esc(n.description)}</p>` : ''}
          ${n.acceptanceCriteria?.length ? `
          <ul style="margin:0;padding-left:16px;display:flex;flex-direction:column;gap:4px">
            ${n.acceptanceCriteria.map(ac => `<li style="font-size:12px;color:#475569">${esc(ac)}</li>`).join('')}
          </ul>` : ''}
        </div>`).join('')}
      </div>
    </div>`];
  }).join('');
}

function buildTaskOutputsContent(taskOutputs: TaskOutputForHTML[]): string {
  const nonEmpty = taskOutputs.filter(t => t.outputs.length > 0);
  if (!nonEmpty.length) {
    return `<div class="card empty-state"><p>No task outputs available for this session.</p></div>`;
  }

  return nonEmpty.map(({ taskName, outputs }) => {
    const outputBlocks = outputs.map(({ type, content }) => {
      if (!content) return '';

      if (type === 'html_artifact' && content.presignedUrl) {
        return `
          <div class="task-output">
            <p class="task-output-type">HTML Artifact</p>
            <p class="task-output-title">${esc(content.fileName || content.title || 'output.html')}</p>
            <a href="${content.presignedUrl}" target="_blank" class="task-output-link">Open HTML file ↗</a>
          </div>`;
      }

      if (type === 'scaffold_bundle' && content.s3Key) {
        return `
          <div class="task-output">
            <p class="task-output-type">Scaffold Bundle</p>
            <p class="task-output-title">${esc(content.fileName || 'bundle.zip')}</p>
            <p class="task-output-meta">Download from ZIP export to access this file.</p>
          </div>`;
      }

      if (type === 'diagram_code') {
        const src = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
        return `
          <div class="task-output">
            <p class="task-output-type">Diagram</p>
            <pre class="task-output-pre">${esc(src)}</pre>
          </div>`;
      }

      if (['competitive_analysis', 'customer_research_report', 'synthesis', 'conversation',
           'research_findings', 'competitive_matrix'].includes(type)) {
        return `
          <div class="task-output">
            <p class="task-output-type">${esc(type.replace(/_/g, ' '))}</p>
            <p class="task-output-title">${esc(content.title || '')}</p>
            ${content.description ? `<p class="task-output-meta">${esc(content.description)}</p>` : ''}
            <p class="task-output-meta" style="color:#00c7c4">Download ZIP export to access the full markdown.</p>
          </div>`;
      }

      if (type === 'bcaf_assessment' && content.overallStatus) {
        const cats = content.categoryScores || {};
        return `
          <div class="task-output">
            <p class="task-output-type">BCAF Assessment</p>
            <p class="task-output-title">Overall: ${esc(content.overallStatus)}</p>
            <div style="display:flex;gap:10px;margin-top:8px;flex-wrap:wrap">
              ${Object.entries(cats).map(([cat, score]: [string, any]) =>
                `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:8px 12px">
                  <p style="font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;letter-spacing:.06em">${esc(cat)}</p>
                  <p style="font-size:13px;font-weight:600;color:#0f172a;margin-top:2px">
                    ${score.passed ?? 0}P / ${score.partial ?? 0}~ / ${score.failed ?? 0}F
                  </p>
                </div>`).join('')}
            </div>
          </div>`;
      }

      return '';
    }).filter(Boolean).join('');

    if (!outputBlocks) return '';

    return `
      <div class="card">
        <h2 class="card-title">${esc(taskName)}</h2>
        ${outputBlocks}
      </div>`;
  }).join('');
}

// ── CSS ───────────────────────────────────────────────────────────────────────

const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    color: #0f172a;
    background: #052831;
    line-height: 1.6;
    height: 100vh;
    overflow: hidden;
  }

  /* ── App shell ── */
  .shell { display: flex; height: 100vh; width: 100vw; }

  /* ── Sidebar ── */
  .sidebar {
    width: 240px;
    flex-shrink: 0;
    background: #031c22;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .sidebar-brand {
    padding: 24px 20px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .sidebar-brand-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #00c7c4;
    margin-bottom: 6px;
  }
  .sidebar-session-name {
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    line-height: 1.3;
  }
  .sidebar-meta {
    font-size: 11px;
    color: #64748b;
    margin-top: 4px;
  }

  .sidebar-nav { padding: 12px 10px; flex: 1; overflow-y: auto; }
  .nav-section-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #334155;
    padding: 8px 10px 4px;
  }
  .nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 8px;
    cursor: pointer;
    color: #94a3b8;
    font-size: 13px;
    font-weight: 500;
    transition: background 0.15s, color 0.15s;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
  }
  .nav-item:hover { background: rgba(255,255,255,0.05); color: #e2e8f0; }
  .nav-item.active { background: rgba(0,199,196,0.12); color: #00c7c4; font-weight: 600; }
  .nav-item .nav-icon { font-size: 15px; width: 18px; text-align: center; flex-shrink: 0; }
  .nav-badge {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    background: rgba(0,199,196,0.2);
    color: #00c7c4;
    border-radius: 99px;
    padding: 1px 7px;
  }

  /* ── Main content ── */
  .main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f0f2f5;
  }
  .main-header {
    background: white;
    border-bottom: 1px solid #e2e8f0;
    padding: 16px 32px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  .main-header-title { font-size: 16px; font-weight: 700; color: #0f172a; }
  .main-header-sub { font-size: 13px; color: #64748b; margin-left: auto; }

  .content-area {
    flex: 1;
    overflow-y: auto;
    padding: 28px 32px 48px;
  }

  /* ── Panels (shown/hidden via JS) ── */
  .panel { display: none; }
  .panel.active { display: block; }

  /* ── Cards ── */
  .card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 24px 28px;
    margin-bottom: 20px;
  }
  .card-title {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #00c7c4;
    margin-bottom: 18px;
  }
  .empty-state { color: #94a3b8; font-size: 13px; text-align: center; padding: 40px; }

  /* ── Decisions ── */
  .decision-list { list-style: none; display: flex; flex-direction: column; gap: 14px; }
  .decision-item { display: flex; gap: 14px; align-items: flex-start; }
  .decision-num {
    flex-shrink: 0; width: 24px; height: 24px; border-radius: 50%;
    background: rgba(0,199,196,0.12); color: #00c7c4;
    font-size: 11px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; margin-top: 1px;
  }
  .decision-text { font-size: 14px; color: #1e293b; line-height: 1.5; }
  .decision-meta { font-size: 12px; color: #94a3b8; margin-top: 3px; }

  /* ── Summary ── */
  .sum-persona-grid { display: flex; flex-direction: column; gap: 14px; }
  .sum-persona { border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; }
  .sum-persona-header { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
  .persona-avatar-sm {
    width: 28px; height: 28px; border-radius: 50%;
    background: rgba(0,199,196,0.15); color: #00c7c4;
    font-size: 12px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .sum-persona-role { font-size: 13px; font-weight: 600; color: #0f172a; }
  .sum-persona-state { font-size: 13px; color: #475569; margin-bottom: 8px; }
  .sum-pain { list-style: none; }
  .sum-pain li { font-size: 12px; color: #64748b; padding-left: 14px; position: relative; margin-bottom: 3px; }
  .sum-pain li::before { content: '•'; position: absolute; left: 0; color: #fca5a5; }
  .sum-helps { list-style: none; margin-top: 6px; }
  .sum-helps li { font-size: 12px; color: #64748b; padding-left: 14px; position: relative; margin-bottom: 3px; }
  .sum-helps li::before { content: '→'; position: absolute; left: 0; color: #00c7c4; }
  .next-steps { list-style: none; display: flex; flex-direction: column; gap: 8px; }
  .next-steps li { font-size: 13px; color: #475569; padding-left: 18px; position: relative; }
  .next-steps li::before { content: '→'; position: absolute; left: 0; color: #00c7c4; }
  .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .stat-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; text-align: center; }
  .stat-val { font-size: 22px; font-weight: 700; color: #0f172a; }
  .stat-unit { font-size: 13px; font-weight: 500; color: #64748b; }
  .stat-lbl { font-size: 11px; color: #94a3b8; margin-top: 2px; }
  .prose { font-size: 13px; color: #475569; white-space: pre-wrap; line-height: 1.8; }

  /* ── Personas ── */
  .persona-card { padding: 0; overflow: hidden; }
  .persona-header {
    display: flex; align-items: center; gap: 14px;
    padding: 18px 24px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  .persona-avatar {
    width: 40px; height: 40px; border-radius: 50%;
    background: rgba(0,199,196,0.15); color: #00c7c4;
    font-size: 16px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .persona-name { font-size: 15px; font-weight: 700; color: #0f172a; }
  .persona-role-lbl { font-size: 12px; color: #64748b; }
  .persona-desc { padding: 14px 24px; font-size: 13px; color: #475569; border-bottom: 1px solid #f1f5f9; }
  .persona-body { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
  .persona-section { padding: 16px 24px; border-bottom: 1px solid #f1f5f9; }
  .persona-section:nth-child(odd) { border-right: 1px solid #f1f5f9; }
  .label { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #94a3b8; margin-bottom: 8px; }
  .dot-list { list-style: none; display: flex; flex-direction: column; gap: 5px; }
  .dot-list li { font-size: 13px; color: #475569; padding-left: 14px; position: relative; }
  .dot-list li::before { content: '•'; position: absolute; left: 0; color: #cbd5e1; }
  .dot-list.pain li::before { color: #fca5a5; }
  .stories-section { padding: 16px 0 8px; border-top: 1px solid #e2e8f0; }
  .story-card { margin: 0 24px 10px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; }
  .story-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; margin-bottom: 6px; }
  .story-text { font-size: 13px; color: #1e293b; flex: 1; line-height: 1.5; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 99px; font-size: 11px; font-weight: 600; color: white; flex-shrink: 0; }
  .ac-list { list-style: none; display: flex; flex-direction: column; gap: 3px; padding-top: 8px; border-top: 1px solid #e2e8f0; margin-top: 6px; }
  .ac-list li { font-size: 12px; color: #64748b; padding-left: 16px; position: relative; }
  .ac-list li::before { content: '✓'; position: absolute; left: 0; color: #00c7c4; font-size: 11px; }

  /* ── Chat / Transcript ── */
  .chat-window {
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 24px 20px;
  }
  .chat-row { display: flex; align-items: flex-end; gap: 10px; padding: 4px 0; }
  .chat-row-human { flex-direction: row-reverse; }
  .chat-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    font-size: 12px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .chat-avatar-human { background: #dbeafe; color: #1d4ed8; }
  .chat-avatar-avatar { background: rgba(0,199,196,0.15); color: #00c7c4; }
  .chat-bubble {
    max-width: 70%;
    border-radius: 16px;
    padding: 10px 14px;
    font-size: 13px;
    line-height: 1.55;
    position: relative;
  }
  .chat-bubble p { color: inherit; }
  .chat-bubble-human {
    background: #1e40af;
    color: white;
    border-bottom-right-radius: 4px;
  }
  .chat-bubble-human .chat-speaker { color: rgba(255,255,255,0.65); }
  .chat-bubble-avatar {
    background: #f0fdfc;
    color: #134e4a;
    border: 1px solid #99f6e4;
    border-bottom-left-radius: 4px;
  }
  .chat-bubble-avatar .chat-speaker { color: #0d9488; }
  .chat-speaker { font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 3px; }
  .chat-time { font-size: 10px; opacity: 0.5; display: block; margin-top: 4px; }
  .chat-time-human { text-align: right; }
  .chat-whiteboard {
    display: flex;
    justify-content: center;
    padding: 6px 0;
  }
  .chat-wb-inner {
    background: #fdf4ff;
    border: 1px dashed #d8b4fe;
    border-radius: 10px;
    padding: 8px 16px;
    max-width: 80%;
    text-align: center;
  }
  .chat-wb-label { font-size: 11px; font-weight: 600; color: #9333ea; display: block; margin-bottom: 3px; }
  .chat-wb-text { font-size: 13px; color: #6b21a8; }

  /* ── Footer ── */
  .sidebar-footer {
    padding: 16px 20px;
    border-top: 1px solid rgba(255,255,255,0.06);
    font-size: 11px;
    color: #334155;
  }
  .sidebar-footer strong { color: #00c7c4; }

  /* ── Images ── */
  .img-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; }
  .img-card {
    border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden;
    text-decoration: none; display: flex; flex-direction: column;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .img-card:hover { border-color: #00c7c4; box-shadow: 0 0 0 2px rgba(0,199,196,0.15); }
  .img-thumb { width: 100%; aspect-ratio: 1; object-fit: cover; display: block; }
  .img-label { font-size: 11px; color: #475569; padding: 6px 8px 4px; font-weight: 500; flex: 1; }
  .img-badge {
    margin: 0 8px 6px; display: inline-block; padding: 1px 6px;
    background: rgba(0,199,196,0.12); color: #00c7c4;
    border-radius: 99px; font-size: 10px; font-weight: 700;
    align-self: flex-start;
  }

  /* ── Task outputs ── */
  .task-output { padding: 14px 0; border-top: 1px solid #f1f5f9; }
  .task-output:first-child { border-top: none; padding-top: 0; }
  .task-output-type {
    font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
    color: #94a3b8; margin-bottom: 4px;
  }
  .task-output-title { font-size: 13px; font-weight: 600; color: #0f172a; margin-bottom: 4px; }
  .task-output-meta { font-size: 12px; color: #64748b; }
  .task-output-link {
    display: inline-block; font-size: 12px; font-weight: 600; color: #00c7c4;
    text-decoration: none; margin-top: 4px;
  }
  .task-output-link:hover { text-decoration: underline; }
  .task-output-pre {
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
    padding: 12px; font-size: 12px; color: #334155; overflow-x: auto;
    white-space: pre-wrap; word-break: break-word; margin-top: 6px;
  }

  @media print {
    body { overflow: visible; height: auto; background: white; }
    .shell { display: block; height: auto; }
    .sidebar { display: none; }
    .main { overflow: visible; }
    .content-area { overflow: visible; padding: 0; }
    .panel { display: block !important; }
    .card { break-inside: avoid; }
  }
`;

// ── HTML assembly ─────────────────────────────────────────────────────────────

function buildHTML(data: ExportData): string {
  const { options } = data;
  const includeTranscript = options.includeTranscript !== false;
  const hasImages = !!(options.images?.length);
  const hasTaskOutputs = !!(options.taskOutputs?.length);

  const hasSummary = !!(data.summary || data.decisions.length);
  const hasTranscript = includeTranscript && data.transcript.length > 0;

  const acceptedCount = data.decisions.filter(d => d.status === 'accepted').length;
  const personaCount = data.requirements?.personas?.length ?? 0;

  const summaryContent = buildSummaryContent(data.decisions, data.summary);
  const personasContent = data.requirements ? buildPersonasContent(data.requirements) : '';
  const nfrsContent = buildNFRsContent(data.requirements);
  const transcriptContent = includeTranscript ? buildTranscriptContent(data.transcript) : '';
  const imagesContent = hasImages ? buildImagesContent(options.images!) : '';
  const taskOutputsContent = hasTaskOutputs ? buildTaskOutputsContent(options.taskOutputs!) : '';

  const nfrCount = data.requirements?.nonFunctionalRequirements?.length ?? 0;

  const panels: Array<{ id: string; label: string; badge?: number | string; show: boolean }> = [
    { id: 'summary', label: 'Summary & Decisions', badge: acceptedCount || undefined, show: hasSummary },
    { id: 'personas', label: 'Personas & Stories', badge: personaCount || undefined, show: !!data.requirements },
    { id: 'nfrs', label: 'NFRs', badge: nfrCount || undefined, show: !!data.requirements },
    { id: 'transcript', label: 'Full Transcript', badge: hasTranscript ? data.transcript.length : undefined, show: includeTranscript },
    { id: 'images', label: 'Images', badge: options.images?.length, show: hasImages },
    { id: 'tasks', label: 'Task Outputs', badge: options.taskOutputs?.length, show: hasTaskOutputs },
  ].filter(p => p.show);

  const defaultPanel = panels[0]?.id ?? 'summary';

  const titlesJS = panels.map(p => `"${p.id}":"${p.label.replace(/"/g, '\\"')}"`).join(',');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${esc(data.sessionName || data.sessionId)} — Version 1 Co-Creation Platform</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
  <style>${CSS}</style>
</head>
<body>
<div class="shell">

  <!-- Sidebar -->
  <aside class="sidebar">
    <div class="sidebar-brand">
      <p class="sidebar-brand-label">Version 1's Co-Creation Platform</p>
      <p class="sidebar-session-name">${esc(data.sessionName || data.sessionId)}</p>
      <p class="sidebar-meta">${formatDate(data.exportedAt)}</p>
    </div>

    <nav class="sidebar-nav">
      <p class="nav-section-label">Session</p>

      ${panels.map(p => `
      <button class="nav-item${p.id === defaultPanel ? ' active' : ''}" onclick="show('${p.id}', this)">
        <span class="nav-icon">${p.id === 'summary' ? '✦' : p.id === 'personas' ? '◎' : p.id === 'transcript' ? '≡' : p.id === 'images' ? '⬜' : '◈'}</span>
        ${esc(p.label)}
        ${p.badge ? `<span class="nav-badge">${p.badge}</span>` : ''}
      </button>`).join('')}
    </nav>

    <div class="sidebar-footer">
      Generated by <strong>Version 1's Co-Creation Platform</strong>
    </div>
  </aside>

  <!-- Main -->
  <main class="main">
    <div class="main-header">
      <p class="main-header-title" id="panel-title">${esc(panels[0]?.label ?? 'Session Export')}</p>
      <p class="main-header-sub">Exported ${formatDate(data.exportedAt)} at ${formatTime(data.exportedAt)}</p>
    </div>
    <div class="content-area">
      ${panels.some(p => p.id === 'summary') ? `
      <div id="panel-summary" class="panel${defaultPanel === 'summary' ? ' active' : ''}">
        ${summaryContent}
      </div>` : ''}
      ${panels.some(p => p.id === 'personas') ? `
      <div id="panel-personas" class="panel${defaultPanel === 'personas' ? ' active' : ''}">
        ${personasContent}
      </div>` : ''}
      ${panels.some(p => p.id === 'nfrs') ? `
      <div id="panel-nfrs" class="panel${defaultPanel === 'nfrs' ? ' active' : ''}">
        ${nfrsContent}
      </div>` : ''}
      ${panels.some(p => p.id === 'transcript') ? `
      <div id="panel-transcript" class="panel${defaultPanel === 'transcript' ? ' active' : ''}">
        ${transcriptContent}
      </div>` : ''}
      ${hasImages ? `
      <div id="panel-images" class="panel${defaultPanel === 'images' ? ' active' : ''}">
        ${imagesContent}
      </div>` : ''}
      ${hasTaskOutputs ? `
      <div id="panel-tasks" class="panel${defaultPanel === 'tasks' ? ' active' : ''}">
        ${taskOutputsContent}
      </div>` : ''}
    </div>
  </main>

</div>
<script>
  const TITLES = {${titlesJS}};
  function show(id, btn) {
    document.querySelectorAll('.panel').forEach(p => p.classList.remove('active'));
    document.querySelectorAll('.nav-item').forEach(b => b.classList.remove('active'));
    document.getElementById('panel-' + id).classList.add('active');
    btn.classList.add('active');
    document.getElementById('panel-title').textContent = TITLES[id] || id;
  }
</script>
</body>
</html>`;
}

// ── Public export function ────────────────────────────────────────────────────

export async function exportSessionHTML(
  sessionId: string,
  sessionName: string | undefined,
  requirements: ProductRequirements | null,
  summary: SessionSummaryData | null,
  options: HTMLExportOptions = {}
): Promise<void> {
  const includeTranscript = options.includeTranscript !== false;

  const [transcriptRes, decisionsRes] = await Promise.all([
    includeTranscript ? api.get(`/api/sessions/${sessionId}/transcript/export`) : Promise.resolve({ data: { transcript: [] } }),
    api.get(`/api/sessions/${sessionId}/decisions`)
  ]);

  const transcript: TranscriptEntry[] = transcriptRes.data?.transcript || [];
  const decisions: SessionDecision[] = decisionsRes.data?.decisions || [];

  const html = buildHTML({
    sessionId,
    sessionName,
    exportedAt: new Date().toISOString(),
    transcript,
    decisions,
    requirements,
    summary,
    options,
  });

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `session-export-${sessionId.slice(0, 8)}-${new Date().toISOString().split('T')[0]}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
